import { Dispatch, SetStateAction } from 'react';
import { calculateInvoice } from '../features/invoice/invoice.slice';
import { transformDeductionToWorkingHour } from '../utils/deduction.utils';

interface UseDeductionSubmitProps {
  currentDeductionFreelancerId: string;
  editingDeductionIndex: number | null;
  deductionsByFreelancer: Record<string, any[]>;
  setDeductionsByFreelancer: Dispatch<SetStateAction<Record<string, any[]>>>;
  serviceTableDataByFreelancer: Record<string, any>;
  pensionDataByFreelancer: Record<string, any>;
  holidayDataByFreelancer: Record<string, any>;
  editingCopiedWorkingHourIndex: number | null;
  setEditingCopiedWorkingHourIndex: Dispatch<SetStateAction<number | null>>;
  setRemovedCopiedWorkingHours: Dispatch<SetStateAction<Set<number>>>;
  selectedFreelancersForInvoice: any[];
  calculatedAmounts: any;
  copiedInvoiceData: any;
  removedCopiedWorkingHours: Set<number>;
  latestRequestIdRef: React.MutableRefObject<number>;
  setCalculatedSubAmount: Dispatch<SetStateAction<any>>;
  preferences: any;
  vat: number;
  currency: string;
  globalData: any;
  deductionType: string;
  customerState: any;
  watch: (field: string) => any;
  dispatch: any;
  createdDate?: string;
  invoiceNumber?: number; // Invoice number from URL for edit mode
}

export const useDeductionSubmit = ({
  currentDeductionFreelancerId,
  editingDeductionIndex,
  deductionsByFreelancer,
  setDeductionsByFreelancer,
  serviceTableDataByFreelancer,
  pensionDataByFreelancer,
  holidayDataByFreelancer,
  editingCopiedWorkingHourIndex,
  setEditingCopiedWorkingHourIndex,
  setRemovedCopiedWorkingHours,
  selectedFreelancersForInvoice,
  calculatedAmounts,
  copiedInvoiceData,
  removedCopiedWorkingHours,
  latestRequestIdRef,
  setCalculatedSubAmount,
  preferences,
  vat,
  currency,
  globalData,
  deductionType,
  customerState,
  watch,
  dispatch,
  createdDate,
  invoiceNumber = 0 // Default to 0 if not provided
}: UseDeductionSubmitProps) => {

  const handleDeductionSubmit = async (deductionData: any): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Get current VAT and currency rate
        const currentVat = preferences?.vat || vat;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === currency)?.globalValue || '1').toString();
        // Transform deduction to workingHour format
        const workingHour = await transformDeductionToWorkingHour(
          deductionData,
          currentVat,
          currencyRate
        );
        
        // Use the freelancerId that was stored when opening the modal
        const targetFreelancerId = currentDeductionFreelancerId;
        
        // Get current service data (optional - can be empty if only adding deductions)
        const serviceData = serviceTableDataByFreelancer[targetFreelancerId];
        const hasServiceData = serviceData && serviceData.workingHours && serviceData.workingHours.length > 0;
        
        if (!hasServiceData) {
          console.warn(`⚠️ No service data available for freelancer ${targetFreelancerId} - proceeding with deductions only`);
        }

        // Get pension and holiday data for this specific freelancer
        const pensionInfo = pensionDataByFreelancer[targetFreelancerId] || { pension: false, pensionAmount: 0 };
        const holidayInfo = holidayDataByFreelancer[targetFreelancerId] || { holidaySavings: false, holidaySavingsFee: 0 };

        // Check if editing a copied working hour from copiedInvoiceData
        if (editingCopiedWorkingHourIndex !== null) {
          // Mark the original as removed
          setRemovedCopiedWorkingHours(prev => new Set([...prev, editingCopiedWorkingHourIndex]));
          // Reset the editing index
          setEditingCopiedWorkingHourIndex(null);
        }

        // Add or update current deduction to state
        const updatedDeductions = {
          ...deductionsByFreelancer,
          [targetFreelancerId]: editingDeductionIndex !== null
            ? (deductionsByFreelancer[targetFreelancerId] || []).map((ded, idx) => 
                idx === editingDeductionIndex ? deductionData : ded
              )
            : [
                ...(deductionsByFreelancer[targetFreelancerId] || []),
                deductionData
              ]
        };

        // Transform all deductions for this freelancer to workingHours
        const freelancerDeductions = updatedDeductions[targetFreelancerId] || [];
        const deductionWorkingHours = [];
        for (const deduction of freelancerDeductions) {
          const deductionWH = await transformDeductionToWorkingHour(
            deduction,
            currentVat,
            currencyRate
          );
          
          deductionWorkingHours.push(deductionWH);
        }

        // Build complete payload matching createInvoice structure (SAME AS WORKING HOURS)
        // Process ALL freelancers to build complete payload
        const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 0;
        const allFreelancerIds = isCollectiveInvoice
          ? selectedFreelancersForInvoice.map((f: any) => (f.userId || f.id).toString())
          : ['default'];
        
        let totalAmountFromWorkingHours = 0;
        const allWorkingHoursForPayload: any[] = [];
        const invoiceFreelancerArr: any[] = [];

        // Process each freelancer
        for (const freelancerId of allFreelancerIds) {
          const freelancerServiceData = serviceTableDataByFreelancer[freelancerId] || { workingHours: [], totalAmount: 0 };
          const freelancerDeductions = updatedDeductions[freelancerId] || [];
          
          // Get actual userId
          const actualUserId = freelancerId === 'default' 
            ? (watch('userId') || 0)
            : parseInt(freelancerId);
          
          // Add service working hours with userId - preserve original VAT if it exists
          const serviceWorkingHoursWithUserId = freelancerServiceData.workingHours.map((wh: any) => ({
            ...wh,
            userId: actualUserId,
            vat: wh.vat != null ? wh.vat : currentVat // Preserve original VAT, fallback to current VAT
          }));
          allWorkingHoursForPayload.push(...serviceWorkingHoursWithUserId);
          
          // Add deduction working hours with userId
          for (const deduction of freelancerDeductions) {
            const deductionWH = await transformDeductionToWorkingHour(
              deduction,
              currentVat,
              currencyRate
            );
            if (deductionWH) {
              allWorkingHoursForPayload.push({
                ...deductionWH,
                userId: actualUserId
              });
            }
          }
          
          // Calculate freelancer amount from working hours
          const deductionAmounts = await Promise.all(
            freelancerDeductions.map(async (d) => {
              const wh = await transformDeductionToWorkingHour(d, currentVat, currencyRate);
              return wh ? wh.amount : 0;
            })
          );
          const freelancerAmount = [...serviceWorkingHoursWithUserId, ...deductionAmounts].reduce((sum: number, wh: any) => {
            return sum + (typeof wh === 'number' ? wh : (wh?.amount || 0));
          }, 0);
          
          totalAmountFromWorkingHours += freelancerAmount;
          
          // Get pension and holiday data for this freelancer
          const freelancerPensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer['default'] || {};
          const freelancerHolidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer['default'] || {};
          
          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: freelancerPensionData?.pension || false,
            pensionAmount: freelancerPensionData?.pensionAmount || 0,
            holidaySavings: freelancerHolidayData?.holidaySavings || false,
            holidaySavingsFee: freelancerHolidayData?.holidaySavingsFee || 0,
          });
        }
        
        // Determine pension and holiday savings
        const pensionToSend = invoiceFreelancerArr.some(f => f.pension);
        const holidaySavingsToSend = invoiceFreelancerArr.some(f => f.holidaySavings);
        const pensionAmountToSend = invoiceFreelancerArr.reduce((sum, f) => sum + (f.pensionAmount || 0), 0);
        const holidaySavingsFeeToSend = invoiceFreelancerArr.reduce((sum, f) => sum + (f.holidaySavingsFee || 0), 0);
        
        // Determine multipleFreelancer
        const multipleFreelancer = invoiceFreelancerArr.length > 1;

        // Build the exact same payload as createInvoice uses
        const payload: any = {
          approvalInvoiceId: 0,
          rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
          rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
          invoiceId: 0,
          sendInvoice: 0, // 0 for calculation (not sending)
          vat: currentVat,
          pension: pensionToSend,
          amount: totalAmountFromWorkingHours || 0,
          isFixed: watch('isFixed') || false,
          currency: currency,
          brfNo: preferences?.brfOrgNumber || '',
          registerAssignment: false,
          showDates: preferences?.showDates === 'yes' || preferences?.showDates === true,
          house: preferences?.accommodationType === 'house',
          holidaySavings: holidaySavingsToSend,
          description: watch('otherDetails')?.customerNotes || watch('description') || '',
          taxValue: calculatedAmounts?.tax || 0,
          totalToPay: calculatedAmounts?.net || 0,
          rutDeduction: deductionType === 'rut',
          rootDeduction: deductionType === 'rot',
          file: null,
          expressPayout: watch('expressPayout') || false,
          workingHours: allWorkingHoursForPayload,
          email: customerState?.selectedCustomer?.email || '',
          apartment: preferences?.accommodationType === 'apartment',
          pensionFee: pensionToSend ? (calculatedAmounts?.pensionPercentage || 0) : 0,
          holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
          tax: watch('incomeTax') || 0,
          rotApartmentNo: preferences?.apartmentHouseNo || '',
          pensionAmount: pensionAmountToSend,
          customerId: customerState?.selectedCustomer?.customerId || 0,
          expenditureForMaterialsAndToolsTaxFreeAmount: 0,
          holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
          amountIncVat: Math.round(calculatedAmounts?.amountIncludingVat || 0),
          secondaryEmailAddress: customerState?.selectedCustomer?.secondaryEmailAddress || '',
          multipleFreelancer: multipleFreelancer,
          currencyRate: globalData?.currencies?.find((item: any) => item.description === currency)?.globalValue || '1',
          invoiceFreelancer: invoiceFreelancerArr,
          locale: 'en-us',
          createdDate: createdDate || new Date().toISOString(),
          invoiceNumber: invoiceNumber // Add invoice number from URL
        };

        // Increment request ID and store it for this specific request
        latestRequestIdRef.current += 1;
        const currentRequestId = latestRequestIdRef.current;

        // Dispatch API call and wait for response
        try {
          const result = await dispatch(calculateInvoice(payload)).unwrap();
          
          // Only update state if this is still the latest request
          // This prevents race conditions where an older API response arrives after a newer one
          if (currentRequestId === latestRequestIdRef.current) {
            setCalculatedSubAmount(result);
            // Update state only after successful API call
            setDeductionsByFreelancer(updatedDeductions);
            resolve();
          } else {
            // Still resolve but don't update state
            resolve();
          }
        } catch (apiError: any) {
          console.error('❌ API Error calling invoice calculation:', apiError);
          // Only update on error if this is still the latest request
          if (currentRequestId === latestRequestIdRef.current) {
            reject(apiError);
          } else {
            // Resolve instead of reject for outdated requests
            resolve();
          }
        }
      } catch (error) {
        console.error('❌ Error processing deduction:', error);
        reject(error);
      }
    });
  };

  return {
    handleDeductionSubmit
  };
};
